import React, { useEffect, useState, useRef } from "react";
import style from "./Dashboard.module.css";
import visitorDashboardBanner from "../../assets/img/visitordashboardbanner.jpg";
import { LineChart } from "../../components/charts/Line";
import { get } from "../../services/api";
import { Link, useNavigate } from "react-router-dom";
import ParentStudentDashboard from "./ParentStudentDashboard";
import FullScreenLoader from "../../components/FullScreenLoader";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import bootstrapPlugin from "@fullcalendar/bootstrap";

function AccountsDashboard() {
  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");
  const roleId = localStorage.getItem("roleId");

  const organizationId =
    localStorage.getItem("organizationId") === "null" ||
    localStorage.getItem("organizationId") === "0"
      ? 0
      : localStorage.getItem("organizationId");

  const centerId =
    localStorage.getItem("centerId") === "0" ||
    localStorage.getItem("centerId") === "null"
      ? 0
      : localStorage.getItem("centerId");

  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [registrationsData, setRegistrationsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [calendarView, setCalendarView] = useState("dayGridMonth");
  const calendarRef = useRef(null);

  // Static events data for accounts department
  const accountsEvents = [
    {
      id: 1,
      title: "Budget Meeting",
      start: new Date(),
      end: new Date(new Date().getTime() + 2 * 60 * 60 * 1000), // 2 hours later
      description: "Monthly budget review meeting with finance team",
      category: "Meeting",
    },
    {
      id: 2,
      title: "Tax Filing Deadline",
      start: new Date(new Date().setDate(new Date().getDate() + 3)),
      allDay: true,
      description: "Quarterly tax filing deadline",
      category: "Deadline",
    },
    {
      id: 3,
      title: "Salary Processing",
      start: (() => {
        const date = new Date();
        date.setDate(date.getDate() + 5);
        date.setHours(10, 0, 0, 0);
        return date;
      })(),
      description: "Process monthly staff salaries",
      category: "Payroll",
    },
    {
      id: 4,
      title: "Audit Preparation",
      start: (() => {
        const date = new Date();
        date.setDate(date.getDate() + 7);
        date.setHours(14, 0, 0, 0);
        return date;
      })(),
      description: "Prepare documents for annual audit",
      category: "Compliance",
    },
    {
      id: 5,
      title: "Vendor Payments",
      start: new Date(new Date().setDate(new Date().getDate() + 10)),
      description: "Process vendor payments for Q1",
      category: "Payments",
    },
  ];

  const getEventCategoryClass = (category) => {
    switch (category) {
      case "Meeting":
        return "event-meeting";
      case "Deadline":
        return "event-deadline";
      case "Payroll":
        return "event-payroll";
      case "Compliance":
        return "event-compliance";
      case "Payments":
        return "event-payments";
      default:
        return "";
    }
  };

  const handleEventClick = (info) => {
    setSelectedEvent({
      title: info.event.title,
      start: info.event.start,
      end: info.event.end,
      allDay: info.event.allDay,
      description: info.event.extendedProps.description,
      category: info.event.extendedProps.category,
      extendedProps: info.event.extendedProps,
    });
  };

  const handleViewChange = (view) => {
    setCalendarView(view);
  };

  useEffect(() => {
    if (calendarRef.current) {
      calendarRef.current.getApi().changeView(calendarView);
    }
  }, [calendarView]);

  const formatDate = (date) => {
    if (!date) return "";
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDashboardData = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(
        `Dashboard/GetAllDashboardStudentStaffRevenueAndCampusAsync?organizationID=${organizationId}&centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setDashboardData(response);
    } catch (error) {
      console.error("Error fetching Dashboard Data:", error);
    }
  };

  const getRegistrationsList = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    setLoading(true);
    try {
      const response = await get(
        `Student/GetPupilsInformationByUserId?userId=${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setRegistrationsData(response || []);
    } catch (error) {
      console.error("Error fetching Registrations:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (roleId === "11") {
      getRegistrationsList();
    } else if (roleId === "9" || roleId === "10") {
      const getStudentsByParentId = async () => {
        try {
          const studentResponse = await get(
            `Student/GetStudentByParentIdAsync?userId=${userId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          if (studentResponse && studentResponse.length > 0) {
            navigate("/parent-student-dashboard");
          }
        } catch (error) {
          console.error("Error fetching student data:", error);
        }
      };
      getStudentsByParentId();
    } else {
      getDashboardData();
    }
  }, [roleId, userId, token, organizationId, centerId, navigate]);

  useEffect(() => {
    if (roleId === "11" && registrationsData.length > 0) {
      navigate("/application-status");
    }
  }, [registrationsData, roleId, navigate]);

  if (roleId === "11") {
    if (loading) {
      return <FullScreenLoader />;
    }

    return (
      <div style={{ minHeight: "400px" }}>
        <img
          className="img-fluid"
          src={visitorDashboardBanner}
          alt="Visitor Dashboard Banner"
        />
        <div className="text-end">
          <Link to="/registration-form" className="btn btn-primary mt-3">
            Apply Now
          </Link>
        </div>
      </div>
    );
  }

  const renderEventDetails = () => {
    if (!selectedEvent) return null;

    return (
      <div className="mt-4 p-3 border rounded bg-light">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <h5>
              <span
                className={`badge ${getEventCategoryClass(
                  selectedEvent.category
                )} me-2`}
              >
                {selectedEvent.category}
              </span>
              {selectedEvent.title}
            </h5>
            <p className="text-muted mb-2">
              {selectedEvent.allDay
                ? "All day event"
                : `${formatDate(selectedEvent.start)}${
                    selectedEvent.end
                      ? ` to ${formatDate(selectedEvent.end)}`
                      : ""
                  }`}
            </p>
          </div>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setSelectedEvent(null)}
          >
            &times;
          </button>
        </div>

        <p className="mb-3">{selectedEvent.description}</p>

        <div className="row">
          {selectedEvent.extendedProps.attendees && (
            <div className="col-md-6">
              <h6>Attendees:</h6>
              <ul className="list-unstyled">
                {selectedEvent.extendedProps.attendees.map((attendee, i) => (
                  <li key={i} className="mb-1">
                    <i className="fas fa-user me-2"></i>
                    {attendee}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {selectedEvent.extendedProps.documents && (
            <div className="col innerhalb-6">
              <h6>Documents:</h6>
              <ul className="list-unstyled">
                {selectedEvent.extendedProps.documents.map((doc, i) => (
                  <li key={i} className="mb-1">
                    <i className="fas fa-file me-2"></i>
                    {doc}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {selectedEvent.extendedProps.priority && (
            <div className="col-md-6">
              <h6>Priority:</h6>
              <span
                className={`badge ${
                  selectedEvent.extendedProps.priority === "High"
                    ? "bg-danger"
                    : "bg-warning"
                }`}
              >
                {selectedEvent.extendedProps.priority}
              </span>
            </div>
          )}

          {selectedEvent.extendedProps.status && (
            <div className="col-md-6">
              <h6>Status:</h6>
              <span className="badge bg-info">
                {selectedEvent.extendedProps.status}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {roleId === "9" || roleId === "10" ? (
        <ParentStudentDashboard />
      ) : roleId === "19" ? (
        <div className={style.accountsDashboard}>
          <h3 className="mb-2">Accounts Dashboard</h3>
          <p className="text-body-tertiary">Financial overview and schedule</p>

          {/* Dashboard Cards Section */}
          <div className="row g-3 mb-3">
            <div className="col-md-6 col-xl-3">
              <div className="infographic-card card card-1">
                <div className="card-body px-3 py-3">
                  <div className="card-texts">
                    <h5 className="card-title mb-3">
                      Total Revenue{" "}
                      <strong>
                        {dashboardData?.summaryStatistics[0]?.totalRevenue || 0}
                      </strong>
                    </h5>
                    <div className="card-content d-flex gap-2">
                      <div>
                        <p className="fs-9 mb-0">Fee Received</p>
                        <h5>
                          {dashboardData?.summaryStatistics[0]?.feeReceived ||
                            0}
                        </h5>
                      </div>
                      <div className="ms-auto">
                        <p className="fs-9 mb-0">Recovery Ratio(%)</p>
                        <h5 className="mb-0 text-end">
                          {dashboardData?.summaryStatistics[0]?.recovery || 0}
                        </h5>
                      </div>
                    </div>
                  </div>
                  <div className="card-icon">
                    <i className="fa fa-hand-holding-dollar fs-6"></i>
                  </div>
                </div>
              </div>
            </div>

            {/* Fixed: Added the missing tiles */}
            <div className="col-md-6 col-xl-3">
              <div className="infographic-card card card-2">
                <div className="card-body px-3 py-3">
                  <div className="card-texts">
                    <h5 className="card-title mb-3">
                      Total Expenses{" "}
                      <strong>
                        {dashboardData?.revenueAndExpense?.[0]?.expense || 0}
                      </strong>
                    </h5>
                    <div className="card-content d-flex gap-2">
                      <div>
                        <p className="fs-9 mb-0">This Month</p>
                        <h5>
                          {dashboardData?.revenueAndExpense?.[0]?.expense || 0}
                        </h5>
                      </div>
                    </div>
                  </div>
                  <div className="card-icon">
                    <i className="fa fa-money-bill-wave fs-6"></i>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6 col-xl-3">
              <div className="infographic-card card card-3">
                <div className="card-body px-3 py-3">
                  <div className="card-texts">
                    <h5 className="card-title mb-3">
                      Profit{" "}
                      <strong>
                        {(dashboardData?.summaryStatistics[0]?.totalRevenue ||
                          0) -
                          (dashboardData?.revenueAndExpense?.[0]?.expense || 0)}
                      </strong>
                    </h5>
                    <div className="card-content d-flex gap-2">
                      <div>
                        <p className="fs-9 mb-0">This Month</p>
                        <h5>5000</h5>
                      </div>
                    </div>
                  </div>
                  <div className="card-icon">
                    <i className="fa fa-chart-line fs-6"></i>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6 col-xl-3">
              <div className="infographic-card card card-4">
                <div className="card-body px-3 py-3">
                  <div className="card-texts">
                    <h5 className="card-title mb-3">
                      Cash Balance <strong>10000</strong>
                    </h5>
                    <div className="card-content d-flex gap-2">
                      <div>
                        <p className="fs-9 mb-0">Available</p>
                        <h5>10000</h5>
                      </div>
                    </div>
                  </div>
                  <div className="card-icon">
                    <i className="fa fa-wallet fs-6"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Charts and Calendar Section */}
          <div className="row g-3">
            <div className="col-lg-6">
              <div className="card h-100">
                <div className="card-body px-3 py-3">
                  <h5 className="mb-2">Student Strength Comparison By Year</h5>
                  <LineChart
                    chartData={
                      dashboardData?.studentStrengthComparisonYearly || []
                    }
                  />
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="card h-100">
                <div className="card-body px-3 py-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Accounts Calendar</h5>
                    <select
                      className="form-select w-auto"
                      value={calendarView}
                      onChange={(e) => handleViewChange(e.target.value)}
                    >
                      <option value="dayGridMonth">Month</option>
                      <option value="timeGridWeek">Week</option>
                      <option value="timeGridDay">Day</option>
                      <option value="listWeek">List</option>
                    </select>
                  </div>

                  <div style={{ height: "400px" }}>
                    <FullCalendar
                      ref={calendarRef}
                      plugins={[
                        dayGridPlugin,
                        timeGridPlugin,
                        listPlugin,
                        interactionPlugin,
                        bootstrapPlugin,
                      ]}
                      initialView={calendarView}
                      headerToolbar={{
                        left: "prev,next today",
                        center: "title",
                        right: "",
                      }}
                      events={accountsEvents.map((event) => ({
                        ...event,
                        className: getEventCategoryClass(event.category),
                        extendedProps: {
                          description: event.description,
                          category: event.category,
                          ...event,
                        },
                      }))}
                      eventClick={handleEventClick}
                      themeSystem="bootstrap"
                      nowIndicator={true}
                      eventTimeFormat={{
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      }}
                      height="100%"
                    />
                  </div>

                  {renderEventDetails()}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-3">
            <h3 className="mb-2">Dashboard</h3>
            <p className="text-body-tertiary">
              Here's what's going on at your business right now!
            </p>
          </div>
          <div className="row g-3 mb-3">
            <div className="col-md-6 col-xl-3">
              <div className="infographic-card card card-1">
                <div className="card-body px-3 py-3">
                  <div className="card-texts">
                    <h5 className="card-title mb-3">
                      Total Students{" "}
                      <strong>
                        {dashboardData?.summaryStatistics[0]?.studentCount || 0}
                      </strong>
                    </h5>
                    <div className="card-content d-flex gap-2">
                      <div>
                        <p className="fs-9 mb-0">Registered</p>
                        <h5>
                          {dashboardData?.summaryStatistics[0]
                            ?.registerdStudentCount || 0}
                        </h5>
                      </div>
                      <div className="ms-auto">
                        <p className="fs-9 mb-0">Conversion Ratio(%)</p>
                        <h5 className="mb-0 text-end">
                          {dashboardData?.summaryStatistics[0]
                            ?.studentConversionRatio || 0}
                        </h5>
                      </div>
                    </div>
                  </div>
                  <div className="card-icon">
                    <i className="fa fa-graduation-cap fs-6"></i>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6 col-xl-3">
              <div className="infographic-card card card-3">
                <div className="card-body px-3 py-3">
                  <div className="card-texts">
                    <h5 className="card-title mb-3">
                      Total Campuses{" "}
                      <strong>
                        {dashboardData?.summaryStatistics[0]?.campusCount || 0}
                      </strong>
                    </h5>
                    <div className="card-content d-flex gap-2">
                      <div>
                        <p className="fs-9 mb-0">Capacity</p>
                        <h5>
                          {dashboardData?.summaryStatistics[0]
                            ?.campusCapacityCount || 0}
                        </h5>
                      </div>
                      <div className="ms-auto">
                        <p className="fs-9 mb-0">Capacity Utilized(%)</p>
                        <h5 className="mb-0 text-end">
                          {dashboardData?.summaryStatistics[0]
                            ?.campusCapacityRatio || 0}
                        </h5>
                      </div>
                    </div>
                  </div>
                  <div className="card-icon">
                    <i className="fa fa-building fs-6"></i>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6 col-xl-3">
              <div className="infographic-card card card-2">
                <div className="card-body px-3 py-3">
                  <div className="card-texts">
                    <h5 className="card-title mb-3">
                      Total Staff{" "}
                      <strong>
                        {dashboardData?.summaryStatistics[0]?.staffCount || 0}
                      </strong>
                    </h5>
                    <div className="card-content d-flex gap-2">
                      <div>
                        <p className="fs-9 mb-0">Teachers</p>
                        <h5>
                          {dashboardData?.summaryStatistics[0]?.teacherCount ||
                            0}
                        </h5>
                      </div>
                      <div className="ms-auto">
                        <p className="fs-9 mb-0">Administration</p>
                        <h5 className="mb-0 text-end">
                          {dashboardData?.summaryStatistics[0]
                            ?.administrationCount || 0}
                        </h5>
                      </div>
                    </div>
                  </div>
                  <div className="card-icon">
                    <i className="fa fa-users-gear fs-6"></i>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6 col-xl-3">
              <div className="infographic-card card card-4">
                <div className="card-body px-3 py-3">
                  <div className="card-texts">
                    <h5 className="card-title mb-3">
                      Total Revenue{" "}
                      <strong>
                        {dashboardData?.summaryStatistics[0]?.totalRevenue || 0}
                      </strong>
                    </h5>
                    <div className="card-content d-flex gap-2">
                      <div>
                        <p className="fs-9 mb-0">Fee Received</p>
                        <h5>
                          {dashboardData?.summaryStatistics[0]?.feeReceived ||
                            0}
                        </h5>
                      </div>
                      <div className="ms-auto">
                        <p className="fs-9 mb-0">Recovery Ratio(%)</p>
                        <h5 className="mb-0 text-end">
                          {dashboardData?.summaryStatistics[0]?.recovery || 0}
                        </h5>
                      </div>
                    </div>
                  </div>
                  <div className="card-icon">
                    <i className="fa fa-hand-holding-dollar fs-6"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Side by Side LineChart and Calendar */}
          <div className="row g-3">
            <div className="col-lg-6">
              <div className="card h-100">
                <div className="card-body px-3 py-3">
                  <h5 className="mb-2">Student Strength Comparison By Year</h5>
                  <LineChart
                    chartData={
                      dashboardData?.studentStrengthComparisonYearly || []
                    }
                  />
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="card h-100">
                <div className="card-body px-3 py-3">
                  <h5 className="mb-2">My Calendar</h5>
                  <div style={{ height: "400px" }}>
                    <FullCalendar
                      plugins={[dayGridPlugin, bootstrapPlugin]}
                      initialView="dayGridMonth"
                      height="100%"
                      themeSystem="bootstrap"
                      // events={[]} // Add events data here if available
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default AccountsDashboard;
